import { useState } from 'react';
import { Plus, GripVertical, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { getVisibleDefs, getExtraCategories, isArrayDef, getAllDefs } from '@/utils/filterDefinitions';
import { useFilterLayout } from '@/store/useFilterLayout';
import { FilterField } from './FilterField';

function AddFilterDrawer({ extraFields, toggleExtra }) {
  const [open, setOpen] = useState(false);
  const categories = getExtraCategories();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="flex-1 rounded-xl" data-testid="add-filter-button">
          <Plus size={16} className="mr-1.5" />Add filters
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[360px] overflow-y-auto">
        <SheetHeader><SheetTitle>Add filters</SheetTitle></SheetHeader>
        <div className="mt-6 space-y-6">
          {Object.entries(categories).map(([cat, defs]) => (
            <div key={cat}>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{cat}</p>
              <div className="mt-2 grid grid-cols-1 gap-2">
                {defs.map((d) => (
                  <label key={d.id} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                    <Checkbox checked={extraFields.includes(d.id)} onCheckedChange={() => toggleExtra(d.id)} data-testid={`add-filter-${d.id}`} />
                    {d.label}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Definition-driven filter controls with visitor-configurable extra fields.
export const FilterControls = ({ filters, onChange }) => {
  const { extraFields, fieldOrder, toggleExtra, removeExtra, setOrder } = useFilterLayout();
  const [dragId, setDragId] = useState(null);
  const defs = getVisibleDefs(extraFields, fieldOrder);

  const clearValue = (def) => {
    if (def.keys) onChange(Object.fromEntries(def.keys.map((k) => [k, ''])));
    else onChange({ [def.key]: isArrayDef(def) ? [] : '' });
  };

  const handleRemove = (def) => { clearValue(def); removeExtra(def.id); };

  const clearAllValues = () => {
    const patch = {};
    getAllDefs().forEach((def) => {
      if (def.keys) def.keys.forEach((k) => { patch[k] = ''; });
      else patch[def.key] = isArrayDef(def) ? [] : '';
    });
    onChange(patch);
  };

  const handleDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = defs.map((d) => d.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return;
    ids.splice(from, 1);
    ids.splice(to, 0, dragId);
    setOrder(ids);
    setDragId(null);
  };

  return (
    <div className="space-y-5">
      {defs.map((def) => (
        <div
          key={def.id}
          draggable
          onDragStart={() => setDragId(def.id)}
          onDragEnd={() => setDragId(null)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => handleDrop(def.id)}
          className={cn('group relative rounded-xl', dragId === def.id && 'opacity-50')}
        >
          <div className="flex items-center gap-1.5">
            <GripVertical size={14} className="cursor-grab text-slate-300 group-hover:text-slate-400" />
            <span className="flex-1" />
            {def.removable && (
              <button type="button" onClick={() => handleRemove(def)} className="text-slate-300 hover:text-red-500" aria-label={`Remove ${def.label}`} data-testid={`remove-filter-${def.id}`}>
                <X size={14} />
              </button>
            )}
          </div>
          <div className="-mt-3"><FilterField def={def} filters={filters} onChange={onChange} /></div>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <AddFilterDrawer extraFields={extraFields} toggleExtra={toggleExtra} />
        <Button variant="ghost" size="sm" className="rounded-xl text-slate-600 hover:text-red-600" onClick={clearAllValues} data-testid="clear-all-filters">
          Clear all
        </Button>
      </div>
    </div>
  );
};
